import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

type MonthYearSelectorProps = {
  selectedMonth: number;
  selectedYear: number;
  viewMonth: number;
  viewYear: number;
  onMonthSelect: (monthIndex: number) => void;
  onViewChange: (year: number, month: number) => void;
};

const MonthYearSelector = ({
  selectedMonth,
  selectedYear,
  viewMonth,
  viewYear,
  onMonthSelect,
  onViewChange,
}: MonthYearSelectorProps) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const handlePrevYear = () => {
    onViewChange(viewYear - 1, viewMonth);
  };

  const handleNextYear = () => {
    if (viewYear + 1 > Math.max(selectedYear, currentYear)) {
      return;
    }
    onViewChange(viewYear + 1, viewMonth);
  };

  const isMonthDisabled = (year: number, month: number) => {
    return year > currentYear || (year === currentYear && month > currentMonth);
  };

  const months = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <button
          className="flex size-8 items-center justify-center rounded-md hover:bg-gray-100"
          onClick={handlePrevYear}
          aria-label="Año anterior"
        >
          ←
        </button>
        <span className="font-bold">{viewYear}</span>
        <button
          className={cn(
            "flex size-8 items-center justify-center rounded-md hover:bg-gray-100",
            viewYear + 1 > Math.max(selectedYear, currentYear) &&
              "pointer-events-none opacity-50"
          )}
          onClick={handleNextYear}
          disabled={viewYear + 1 > Math.max(selectedYear, currentYear)}
          aria-label="Siguiente año"
        >
          →
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1 md:gap-2">
        {months.map((month, index) => {
          const isDisabled = isMonthDisabled(viewYear, index);
          const isSelected = viewYear === selectedYear && index === selectedMonth;
          return (
            <Button
              key={index}
              size="sm"
              variant={isSelected ? "main" : "outline"}
              onClick={() => onMonthSelect(index)}
              disabled={isDisabled}
              className={cn(
                isDisabled && "opacity-40 cursor-not-allowed",
                !isSelected && "border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              {month}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthYearSelector;
